import DateFormat from '@/Enums/Format/DateFormat.enum';
import { IChangeLogHistoryGridRow } from '@/Interfaces/ChangeLogHistory.interface';
import { IControlOption } from '@/Interfaces/ControlOption.interface';
import { IAbortSignalThunkArg, IRejectValueThunkConfig } from '@/Interfaces/Slices/AsyncThunk.interface';
import { getOptionsFromCampaignsList } from '@/helpers/campaigns';
import { formatChangeLogHistoryResponseData, transformChangeLogHistoryFilters } from '@/helpers/changeLogHistory';
import { isRoleAllowedWhitelabelSelect } from '@/helpers/roles';
import { RootState } from '@/store';
import { SearchChangeLogHistoryFilters } from '@/types/changeLogHistory.types';
import { downloadBlob } from '@/utils/download';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import moment from 'moment';
import { exportChangeLogHistory, fetchChangeLogHistory } from '../api/changeLogHistory.api';
import { fetchBeeswaxCampaigns } from '../api/reportBuilder.api';
import { pendingCaseHandler, rejectedCaseHandler } from '../asyncThunkHandlers';
import { selectRibeyeAllClients, selectRibeyeAllWhitelabels } from '../selectors/sharedApi.selectors';

enum actionPrefix {
  REQUEST = 'changeLogHistorySlice/request',
  EXPORT = 'changeLogHistorySlice/export',
  GET_CAMPAIGN_OPTIONS = 'changeLogHistorySlice/getCampaignOptions',
}

interface IState {
  data: IChangeLogHistoryGridRow[];
  dataRequestId: string | null;
  exportRequestId: string | null;
  campaignOptions: IControlOption[];
  campaignOptionsRequestId: string | null;
  total: number;
  filters: SearchChangeLogHistoryFilters;
}

const initialState: IState = {
  data: [],
  dataRequestId: null,
  exportRequestId: null,
  campaignOptions: [],
  campaignOptionsRequestId: null,
  total: 0,
  filters: {
    page: 1,
    limit: 15,
    search: '',
    sort: null,
    whitelabels: [],
    clients: [],
    advertisers: [],
    campaigns: [],
    startDate: moment().subtract({ days: 6 }).format(DateFormat.Iso8601),
    endDate: moment().format(DateFormat.Iso8601),
  },
};

const changeLogHistorySlice = createSlice({
  name: 'changeLogHistory',
  initialState,
  reducers: {
    setFilters: (state, { payload }: { payload: Partial<SearchChangeLogHistoryFilters> }) => {
      const keys = Object.keys(payload);
      const isPaginationOnly = keys.length > 0 && keys.every(k => k === 'page' || k === 'limit');
      state.filters = {
        ...state.filters,
        ...payload,
        page: isPaginationOnly ? (payload.page ?? state.filters.page) : 1,
      };
    },
    resetChangeLogHistoryPageState: () => initialState,
  },
  extraReducers: builder => {
    builder
      .addCase(getLogHistory.fulfilled, (state, { meta, payload }) => {
        if (state.dataRequestId !== meta.requestId) return;
        state.dataRequestId = null;
        state.data = payload.data;
        state.total = payload.total;
        if (!payload.data.length && state.filters.page > 1) {
          state.filters.page = 1;
        }
      })
      .addCase(getLogHistory.pending, pendingCaseHandler('data'))
      .addCase(getLogHistory.rejected, rejectedCaseHandler('data'))
      .addCase(exportLogHistory.fulfilled, (state, { meta }) => {
        if (state.exportRequestId !== meta.requestId) return;
        state.exportRequestId = null;
      })
      .addCase(exportLogHistory.pending, pendingCaseHandler('export'))
      .addCase(exportLogHistory.rejected, rejectedCaseHandler('export'))
      .addCase(getCampaignOptions.fulfilled, (state, { meta, payload }) => {
        if (state.campaignOptionsRequestId !== meta.requestId) return;
        state.campaignOptionsRequestId = null;
        state.campaignOptions = payload;
      })
      .addCase(getCampaignOptions.pending, pendingCaseHandler('campaignOptions'))
      .addCase(getCampaignOptions.rejected, rejectedCaseHandler('campaignOptions'));
  },
});

export default changeLogHistorySlice.reducer;

export const { setFilters, resetChangeLogHistoryPageState } = changeLogHistorySlice.actions;

export const getLogHistory = createAsyncThunk<
  { data: IChangeLogHistoryGridRow[]; total: number },
  IAbortSignalThunkArg,
  IRejectValueThunkConfig
>(actionPrefix.REQUEST, async (_, { rejectWithValue, getState }) => {
  const {
    changeLogHistory: { filters },
  } = getState() as RootState;

  const transformedFilters = transformChangeLogHistoryFilters(filters);

  try {
    const response = await fetchChangeLogHistory(transformedFilters);

    return formatChangeLogHistoryResponseData(response);
  } catch (err) {
    if (err instanceof Error) {
      return rejectWithValue(err);
    }
    return rejectWithValue(new Error('An unknown error occurred'));
  }
});

export const exportLogHistory = createAsyncThunk<void, IAbortSignalThunkArg, IRejectValueThunkConfig>(
  actionPrefix.EXPORT,
  async (_, { rejectWithValue, getState }) => {
    const {
      changeLogHistory: { filters },
    } = getState() as RootState;

    const transformedFilters = transformChangeLogHistoryFilters(filters);
    try {
      const response = await exportChangeLogHistory(transformedFilters);
      const fileName = `change_log_history_${new Date().toISOString()}.csv`;
      downloadBlob(response, fileName);
    } catch (err) {
      if (err instanceof Error) {
        return rejectWithValue(err);
      }
      return rejectWithValue(new Error('An unknown error occurred'));
    }
  },
);

export const getCampaignOptions = createAsyncThunk<IControlOption[], IAbortSignalThunkArg, IRejectValueThunkConfig>(
  actionPrefix.GET_CAMPAIGN_OPTIONS,
  async ({ signal }, { rejectWithValue, getState }) => {
    const state = getState() as RootState;
    const { filters } = state.changeLogHistory;
    const { role } = state.me;

    let advertiserIds: number[] = [];

    if (filters.advertisers?.length) {
      advertiserIds = filters.advertisers.map(({ value }) => Number(value)).filter(Boolean);
    } else if (role) {
      // No advertiser filter — fall back to all advertisers in the user's scope.
      const sourceAdvertisers = isRoleAllowedWhitelabelSelect(role)
        ? selectRibeyeAllWhitelabels(state).flatMap(wl => (wl.clients ?? []).flatMap(c => c.advertisers ?? []))
        : selectRibeyeAllClients(state).flatMap(c => c.advertisers ?? []);

      const seen = new Set<number>();
      for (const adv of sourceAdvertisers) {
        const id = Number(adv.value);
        if (id && !seen.has(id)) {
          seen.add(id);
          advertiserIds.push(id);
        }
      }
    }

    if (!advertiserIds.length) {
      return [];
    }

    try {
      const response = await fetchBeeswaxCampaigns(advertiserIds, signal);
      return getOptionsFromCampaignsList(response);
    } catch (err) {
      if (err instanceof Error) {
        return rejectWithValue(err);
      }
      return rejectWithValue(new Error('An unknown error occurred'));
    }
  },
);
